import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const PosProfileContext = createContext(null);

/**
 * Provider that holds the selected POS Profile and its full config
 * for the duration of the POS session.
 *
 * State:
 * - profile: { name, warehouse, company, currency, ... }
 * - profileData: full profile data from get_pos_profile_data
 * - openingEntry: current POS Opening Entry name
 * - cashMovements: [{ id, type, amount, note, createdAt }]
 */
export function PosProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [openingEntry, setOpeningEntry] = useState(null);
  const [cashMovements, setCashMovements] = useState([]);

  /** Add a cash movement (type: 'in' | 'out'). */
  const addCashMovement = useCallback(({ type, amount, note }) => {
    setCashMovements((prev) => [
      ...prev,
      {
        id: uuidv4(),
        type,
        amount: parseFloat(amount) || 0,
        note: note || '',
        createdAt: new Date().toISOString(),
      },
    ]);
  }, []);

  /** Remove a cash movement by id. */
  const removeCashMovement = useCallback((id) => {
    setCashMovements((prev) => prev.filter((m) => m.id !== id));
  }, []);

  /** Net cash adjustment (in minus out). */
  const cashMovementNet = useMemo(
    () =>
      cashMovements.reduce(
        (sum, m) => sum + (m.type === 'in' ? m.amount : -m.amount),
        0,
      ),
    [cashMovements],
  );

  const value = useMemo(
    () => ({
      profile,
      setProfile,
      profileData,
      setProfileData,
      openingEntry,
      setOpeningEntry,
      cashMovements,
      setCashMovements,
      addCashMovement,
      removeCashMovement,
      cashMovementNet,
    }),
    [profile, profileData, openingEntry, cashMovements, addCashMovement, removeCashMovement, cashMovementNet],
  );

  return (
    <PosProfileContext.Provider value={value}>
      {children}
    </PosProfileContext.Provider>
  );
}

/**
 * Hook to access the POS Profile context.
 * Must be used within a PosProfileProvider.
 */
export function usePosProfile() {
  const ctx = useContext(PosProfileContext);
  if (!ctx) {
    throw new Error('usePosProfile must be used within a PosProfileProvider');
  }
  return ctx;
}
